import React, { useState, useEffect } from 'react';
import { listDataSources, deleteDataSource, updateDataSource, getDataSourceBatchData, DataSource } from '../api/client';
import { useAuth } from '../context/AuthContext';
import DataSourceConfig from '../components/DataSourceConfig';
import './Overview.css';

const DataSourcesOverview: React.FC = () => {
  const { canEdit, canDelete } = useAuth();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'type'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [previewData, setPreviewData] = useState<Record<string, any>[] | null>(null);
  const [previewColumnOrder, setPreviewColumnOrder] = useState<string[]>([]);
  const [previewName, setPreviewName] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    setLoading(true);
    try {
      const data = await listDataSources();
      setDataSources(data);
    } catch (err) {
      // Error silently handled
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ds: DataSource) => {
    setEditingId(ds.id);
    setEditName(ds.name);
  };

  const handleSaveEdit = async (id: string) => {
    try {
      await updateDataSource(id, {
        name: editName,
      });
      alert('Datenvorlage erfolgreich aktualisiert');
      setEditingId(null);
      loadDataSources();
    } catch (err: any) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Möchten Sie die Datenvorlage "${name}" wirklich löschen?`)) {
      return;
    }

    try {
      await deleteDataSource(id);
      alert('Datenvorlage erfolgreich gelöscht');
      loadDataSources(); // Reload list
    } catch (err: any) {
      alert('Fehler beim Löschen: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDataSourceCreated = (newDataSource: DataSource) => {
    setDataSources([...dataSources, newDataSource]);
    setShowCreateForm(false);
    alert(`Datenvorlage "${newDataSource.name}" erfolgreich erstellt!`);
  };

  const handlePreview = async (ds: DataSource) => {
    setPreviewLoading(true);
    setPreviewName(ds.name);
    try {
      const response = await getDataSourceBatchData(ds.id);
      setPreviewData(response.data);
      setPreviewColumnOrder(response.column_order || []);
    } catch (err: any) {
      alert('Fehler beim Laden der Daten: ' + (err.response?.data?.error || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewData(null);
    setPreviewColumnOrder([]);
    setPreviewName('');
  };

  const toggleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'created' ? 'desc' : 'asc');
    }
  };

  const sortedDataSources = [...dataSources].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'name') {
      cmp = a.name.localeCompare(b.name, 'de');
    } else if (sortBy === 'type') {
      cmp = a.type.localeCompare(b.type);
    } else {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  if (loading) {
    return (
      <div className="overview-container">
        <h1>Lade Datenvorlagen...</h1>
      </div>
    );
  }

  return (
    <div className="overview-container">
      <div className="overview-header">
        <div>
          <h1>Datenvorlagen</h1>
          <p className="overview-description">
            Alle verfügbaren Datenvorlagen für Ihre Projekte. Hier werden nur die Datenstrukturen gespeichert.
          </p>
        </div>
        {canEdit && (
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕ Abbrechen' : '+ Neue Datenvorlage'}
          </button>
        )}
      </div>

      {showCreateForm && (
        <div style={{
          background: '#f9f9f9',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: '2px solid #667eea'
        }}>
          <DataSourceConfig onSuccess={handleDataSourceCreated} />
        </div>
      )}

      {dataSources.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Sortieren:</span>
          {([['name', 'Name'], ['created', 'Erstellt'], ['type', 'Typ']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleSort(key)}
              className="btn-small"
              style={{
                background: sortBy === key ? '#006355' : '#e0e0e0',
                color: sortBy === key ? 'white' : '#333',
              }}
            >
              {label} {sortBy === key && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          ))}
        </div>
      )}

      {dataSources.length === 0 && !showCreateForm ? (
        <div className="empty-state">
          <h3>Keine Datenvorlagen vorhanden</h3>
          {canEdit ? (
            <>
              <p>Erstellen Sie Ihre erste Datenvorlage, um loszulegen.</p>
              <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
                Erste Datenvorlage erstellen
              </button>
            </>
          ) : (
            <p>Es wurden noch keine Datenvorlagen erstellt.</p>
          )}
        </div>
      ) : dataSources.length > 0 ? (
        <div className="grid-layout">
          {sortedDataSources.map((ds) => (
            <div
              key={ds.id}
              className="card"
              onClick={() => canEdit && editingId !== ds.id && handleEdit(ds)}
              style={{ cursor: canEdit && editingId !== ds.id ? 'pointer' : 'default' }}
            >
              {editingId === ds.id ? (
                <>
                  <div className="card-header">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        fontSize: '1rem',
                        border: '2px solid #006355',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                  <div className="card-meta">
                    <div>
                      <strong>Typ:</strong> {ds.type}
                    </div>
                  </div>
                  <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleSaveEdit(ds.id)}
                      className="btn-small btn-primary"
                      style={{ background: '#006355' }}
                    >
                      💾 Speichern
                    </button>
                    <button onClick={handleCancelEdit} className="btn-small btn-secondary">
                      ✕ Abbrechen
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="card-header">
                    <h3>{ds.name}</h3>
                    <span className={`badge badge-${ds.type}`}>{ds.type.toUpperCase()}</span>
                  </div>
                  <div className="card-meta">
                    <div>
                      <strong>Typ:</strong> {ds.type}
                    </div>
                    <div>
                      <strong>Erstellt:</strong>{' '}
                      {new Date(ds.created_at).toLocaleDateString('de-DE')}
                    </div>
                  </div>
                  <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handlePreview(ds)} className="btn-small btn-secondary">
                      👁️ Preview
                    </button>
                    {canDelete && (
                      <button onClick={() => handleDelete(ds.id, ds.name)} className="btn-small btn-danger">
                        🗑️ Löschen
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Preview Modal */}
      {(previewData || previewLoading) && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Datenvorschau: {previewName}</h2>
              <button onClick={closePreview} className="btn-close">✕</button>
            </div>
            <div className="modal-body">
              {previewLoading ? (
                <p>Lade Daten...</p>
              ) : previewData && previewData.length > 0 ? (
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {(previewColumnOrder.length > 0 ? previewColumnOrder : Object.keys(previewData[0])).map((key) => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 100).map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          {(previewColumnOrder.length > 0 ? previewColumnOrder : Object.keys(row)).map((key) => (
                            <td key={key}>{String(row[key] ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 100 && (
                    <p className="preview-info">Zeige erste 100 von {previewData.length} Datensätzen</p>
                  )}
                </div>
              ) : (
                <p>Keine Daten vorhanden</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourcesOverview;
